import {compareVersionNumbers, toInteger} from "@webmate-web-core/shared/utils/utils";
import {DeviceProperties, DevicePropertyNames, PlatformFamily} from '@webmate-web-core/shared/types/devices/device-properties';

/**
 * Represents a Platform (note that this is definitely not the same as an actual Template)
 */
//TODO: make platformType field of Platform into this type
// note: many part of code in package.ts, logo-resolver.service.ts, etc. depend on platformType being a string so it will require proper testing
export type PlatformType = "WINDOWS" | "MACOS" | "UBUNTU" | "iOS" | "Android" | "Unknown";

export class Platform {
    constructor( public platformType: string, public readonly platformVersion: string, public readonly platformArchitecture?: string, public platformName?: string) {};

    getFullName(): string {
        let fullName = this.platformType + " " + this.platformVersion;
        if (this.platformName) {
          fullName += " on: " + this.platformName;
        }

        return fullName;
    }

    getShortName(): string {
        return this.shortPlatformType() + " " + this.platformVersion;
    }

    toBackendString(): string {
        let suffix = !!this.platformArchitecture ? `_${this.platformArchitecture}` : '';
        return `${this.platformType}_${this.platformVersion}${suffix}`;
    }

    shortPlatformType(): string {
        if (this.platformType === 'WINDOWS') {
            return 'Win';
        } else {
            return this.platformType;
        }
    }

    getPlatformFamily(): PlatformFamily {
        if (this.platformType?.toLowerCase() === 'android' || this.platformType?.toLowerCase() === 'ios') {
            return 'mobile';
        } else {
            return 'desktop';
        }
    }

    equals(other: Platform) {
        return this.getFullName() === other.getFullName();
    }

    public static getUnknown(): Platform {
        return new Platform("Unknown", "Platform");
    }
}

/**
 * Helper function to convert BackEnd Strings (for instance, Devices and Templates use Strings)
 */
export function platformFromString(str: string): Platform {
    let platformType: string = str.substring(0, str.indexOf("_"));

    let tmp: string = str.substring(platformType.length + 1);
    let platformVersion: string;
    let platformArchitecture: string | undefined;

    let delimIndex = tmp.indexOf("_");
    if (delimIndex === -1) {
        platformVersion = tmp;
    } else {
        platformVersion = tmp.substring(0, delimIndex);
        platformArchitecture = tmp.substring(platformVersion.length + 1);
    }

    return new Platform(platformType, platformVersion, platformArchitecture);
}

/**
 * Helper function to convert BackEnd Objects (for instance, Jobs usually use Objects)
 */
export function platformFromObject(obj: any): Platform  {
    return new Platform(obj['platformType'], obj['platformVersion'], obj['platformArchitecture']);
}

//reverse order so new and potentially unknown values are inserted at the end of the list
const platformTypeOrder: string[] = ["ubuntu", "macos", "windows", "android", "ios"];
const macOSVersionOrder: string[] = ["10,8", "10.9", "10.10", "10.11", "sierra", "highsierra", "mojave", "catalina", "bigsur", "monterey", "ventura", "sonoma", "sequoia", "tahoe"];

export function platformVersionOrder(versionA: string, versionB: string, platformType: string): number {
    if (platformType === "macos") {
        return getIndex(versionA) - getIndex(versionB);
    } else {
        return compareVersionNumbers(versionA, versionB);
    }
}

export function platformComparator(a: Platform, b: Platform): number  {
    let order = platformTypeOrder.indexOf(a.platformType.toLowerCase()) - platformTypeOrder.indexOf(b.platformType.toLowerCase());

    if (order === 0) {
        order = platformVersionOrder(a.platformVersion.toLowerCase(), b.platformVersion.toLowerCase(), a.platformType.toLowerCase());
    }

    if (order === 0) {
        order = toInteger(a.platformArchitecture) - toInteger(b.platformArchitecture);
    }

    return order;
}

function getIndex(input: string): number {
    let index = macOSVersionOrder.indexOf(input);
    if (index === -1) { // if the searched version is unknown the index is -1, in this case we want it above all other versions
        index = macOSVersionOrder.length + 1;
    }
    return index;
}
